
import { useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import RelocationFeature from './components/RelocationFeature';
import OurFocus from './components/OurFocus';
import BespokeAccommodation from './components/BespokeAccommodation';
import MuslimFriendly from './components/MuslimFriendly';
import ExclusiveDeals from './components/ExclusiveDeals';
import PersonalisedAssistance from './components/PersonalisedAssistance';
import Destinations from './components/Destinations';
import Testimonials from './components/Testimonials';
import CTA from './components/CTA';
import Footer from './components/Footer';

export default function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.alwaz.travel';
    const scripts: HTMLScriptElement[] = [];

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Alwaz Travels',
        url: siteUrl,
        description: 'London\'s trusted Muslim-friendly travel agency offering Umrah packages, discounted worldwide flights, Saudi relocation services, and bespoke holiday packages.',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/travel-guides?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'TravelAgency',
        name: 'Alwaz Travels',
        alternateName: 'Nex Pro Travels Limited',
        url: siteUrl,
        logo: `${siteUrl}/vite.svg`,
        description: 'UK-based Muslim-friendly travel agency specialising in Umrah packages, Hajj tours, discounted worldwide flights, Saudi relocation, and bespoke holiday planning.',
        telephone: '+442034884455',
        email: 'contact@alwaztravels.com',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'London',
          addressRegion: 'England',
          addressCountry: 'GB',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 51.5074,
          longitude: -0.1278,
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Saturday'],
            opens: '10:00',
            closes: '16:00',
          },
        ],
        priceRange: '££',
        currenciesAccepted: 'GBP',
        paymentAccepted: 'Cash, Credit Card, Bank Transfer',
        areaServed: {
          '@type': 'Country',
          name: 'United Kingdom',
        },
        sameAs: [
          'https://wa.me/447362505895',
        ],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Travel Services',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Umrah Packages' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Hajj Tours' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Discounted Air Tickets' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Saudi Relocation Services' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Visit London Packages' } },
          ],
        },
      },
    ];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });

    // Update page-level meta
    document.title = 'Alwaz Travels London | Muslim-Friendly Travel, Umrah, Flights & Relocation';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Alwaz Travels – London\'s trusted Muslim-friendly travel agency. Book Umrah packages, discounted worldwide flights, Saudi relocation services, and bespoke holiday packages. Call +44 7362 505895.');

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />
      <About />
      <RelocationFeature />
      <OurFocus />
      <BespokeAccommodation />
      <MuslimFriendly />
      <ExclusiveDeals />
      <PersonalisedAssistance />
      <Destinations />
      <Testimonials />
      <CTA />
      <Footer />
    </div>
  );
}
